import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import store from "@/redux/store";
import { setCustomerCareChatId as setUserChatId, setCustomerCareMessages as setUserMessages } from "@/redux/slices/userSlice";
import { setCustomerCareChatId as setVendorChatId, setCustomerCareMessages as setVendorMessages } from "@/redux/slices/vendorSlice";
import { api as userApi } from "@/utils/api";
import { api as vendorApi } from "@/utils/vendorApi";
import { selectSupportAccount, type SupportAccountType } from "./supportAccount";
import { useSupportAccount } from "./useSupportAccount";
import { initialSupportChatState, SupportChatSession, type SupportChatState } from "./supportChatSession";
import { useChatNotifications } from "@/hook/useChatNotifications";

const SOCKET_URL = "https://ilosiwaju-mbaay-2025.com";
const browserStorage = {
  getItem: (key: string) => localStorage.getItem(key),
  setItem: (key: string, value: string) => localStorage.setItem(key, value),
  removeItem: (key: string) => localStorage.removeItem(key),
};

type Permission = NotificationPermission | "unsupported";
const readNotificationPermission = (): Permission =>
  typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : "unsupported";

export const useSupportChat = (
  isOpen: boolean,
  onClose: () => void,
  accountType?: SupportAccountType,
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const account = useSupportAccount(accountType);
  const role = account?.type;
  const senderId = account?.senderId;
  const token = account?.token;
  const identity = role && senderId ? `${role}:${senderId}` : null;
  const api = role === "vendor" ? vendorApi : userApi;
  const sessionRef = useRef<SupportChatSession | null>(null);
  const notifications = useChatNotifications({ channel: "customer_care", role: role ?? accountType ?? "user", id: senderId ?? "" });
  const rememberMessages = notifications.rememberMessages;
  const supportChatId = account?.chatId;
  const supportMessages = account?.messages;
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const accountTypeRef = useRef(accountType);
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const navigateRef = useRef(navigate);
  accountTypeRef.current = accountType;
  isOpenRef.current = isOpen;
  onCloseRef.current = onClose;
  navigateRef.current = navigate;

  const [snapshot, setSnapshot] = useState<{
    identity: string | null;
    state: SupportChatState;
  }>(() => ({ identity: null, state: initialSupportChatState() }));
  const [notificationPermission, setNotificationPermission] =
    useState<Permission>(readNotificationPermission);

  useEffect(() => {
    setSnapshot({ identity, state: initialSupportChatState() });
    if (!senderId || !role) return;
    const selected = selectSupportAccount(store.getState(), accountTypeRef.current);
    if (!selected || selected.senderId !== senderId || selected.type !== role) return;

    const isCurrentAccount = () => {
      const current = selectSupportAccount(store.getState(), accountTypeRef.current);
      return current?.senderId === senderId && current.type === role;
    };
    const session = new SupportChatSession({
      accountType: role,
      senderId,
      chatId: selected.chatId,
      api,
      storage: browserStorage,
      createSocket: accessToken => io(SOCKET_URL, {
        auth: { token: `Bearer ${accessToken}` },
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 10000,
      }),
      isCurrentAccount,
      getMessages: () => store.getState()[role].customerCareMessages || [],
      setMessages: messages => {
        if (!isCurrentAccount()) return;
        dispatch(role === "vendor" ? setVendorMessages(messages) : setUserMessages(messages));
      },
      setChatId: chatId => {
        if (!isCurrentAccount()) return;
        dispatch(role === "vendor" ? setVendorChatId(chatId) : setUserChatId(chatId));
      },
      onState: state => setSnapshot({ identity, state }),
      onIncoming: message => {
        if (isOpenRef.current && !document.hidden) return;
        const supportChatId = store.getState()[role].customerCareChatId;
        if (supportChatId) {
          notificationsRef.current.notifyIncoming({ id: message.id, chatId: supportChatId, senderId: message.authorId ?? "" });
        }
        if (readNotificationPermission() === "granted") {
          try {
            new Notification("Mbaay Support", {
              body: message.content,
              tag: `mbaay-support-${message.id}`,
            });
          } catch { /* Some browsers require service workers for notifications. */ }
        }
      },
    });
    sessionRef.current = session;
    session.updateToken(selected.token);

    return () => {
      session.dispose();
      if (sessionRef.current === session) sessionRef.current = null;
    };
  }, [api, dispatch, identity, role, senderId]);

  useEffect(() => {
    if (token) sessionRef.current?.updateToken(token);
  }, [token, role, senderId]);

  useEffect(() => {
    if (!isOpen) {
      sessionRef.current?.stopTyping();
      return;
    }
    if (!senderId || !role) {
      try { localStorage.setItem("mbaay:customer-care:reopen-after-login", "true"); } catch { /* Optional login intent. */ }
      onCloseRef.current();
      navigateRef.current("/selectpath");
      return;
    }
    const session = sessionRef.current;
    let cancelled = false;
    // Strict Mode cleans up its first effect before this task, preventing a
    // discarded mount from creating an extra conversation on the server.
    queueMicrotask(() => {
      if (!cancelled && sessionRef.current === session) void session?.start();
    });
    return () => { cancelled = true; };
  }, [isOpen, senderId, role]);

  useEffect(() => {
    if (!supportChatId || !supportMessages) return;
    rememberMessages(supportMessages.map(message => ({ id: message.id, chatId: supportChatId })));
  }, [supportChatId, supportMessages, rememberMessages]);

  const send = useCallback((content: string) =>
    sessionRef.current?.send(content) ?? Promise.resolve(false), []);
  const retry = useCallback(() => sessionRef.current?.start() ?? Promise.resolve(), []);
  const typing = useCallback((hasText: boolean) => sessionRef.current?.typing(hasText), []);
  const refreshHistory = useCallback(() =>
    sessionRef.current?.refreshHistory() ?? Promise.resolve(), []);
  const refreshNotifications = useCallback(() =>
    sessionRef.current?.refreshNotifications() ?? Promise.resolve(), []);
  const enableNotifications = useCallback(async () => {
    if (readNotificationPermission() === "unsupported") return;
    try {
      setNotificationPermission(await Notification.requestPermission());
    } catch {
      setNotificationPermission(readNotificationPermission());
    }
  }, []);

  return {
    ...(snapshot.identity === identity ? snapshot.state : initialSupportChatState()),
    messages: account?.messages ?? [],
    role,
    senderId,
    send,
    retry,
    typing,
    refreshHistory,
    refreshNotifications,
    notificationPermission,
    enableNotifications,
    soundsMuted: notifications.muted,
    toggleSoundsMuted: notifications.toggleMuted,
  };
};
